import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMagic } from '../../contexts/MagicContext';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, RootState } from '../../store';
import { object, string } from 'yup';
import { useFormik } from 'formik';
import Typography from '../../components/Typography/Typography';
import TextField from '../../components/TextField/TextField';
import Button from '../../components/Button/Button';
import { ClipLoader } from 'react-spinners';
import { emailVerification, azureVerification } from '../../http';
import ASSETS from '../../assets';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import {
  cancelMagicOtpSession,
  canReuseOtpSession,
  clearOtpSessionStorage,
  markOtpSent,
  MAGIC_OTP_LIFESPAN_SEC,
  prepareMagicOtpToken,
} from '../../utils/magicOtp';

const schema = object({
  email: string()
    .email()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      'Invalid email address'
    )
    .test(
      'no-multiple-tlds',
      'Invalid email address with multiple TLDs',
      value => {
        if (!value) return false;

        const domainPart = value.split('@')[1];
        if (!domainPart) return false;

        const tlds = domainPart.split('.');
        const lastTLD = tlds[tlds.length - 1];

        return tlds.indexOf(lastTLD) === tlds.length - 1 && tlds.length < 4;
      }
    )
    .required(),
});

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<Dispatch>();
  const { magic } = useMagic();
  const { instance } = useMsal();
  const { tokenPromise } = useSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState(false);
  const [showErrorScreen, setShowErrorScreen] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);

  const handleMicrosoftLogin = async () => {
    try {
      console.log('🚀 Starting Microsoft OAuth flow with MSAL...');
      setLoading(true);
      setIsWidgetOpen(true);
      
      // Use MSAL to initiate login
      const response = await instance.loginPopup(loginRequest);
      console.log('✅ MSAL login successful:', response);
      
      // Store the JWT token with name "microsoft_jwt"
      if (response.accessToken) {
        localStorage.setItem('microsoft_jwt', response.accessToken);
        sessionStorage.setItem('microsoft_jwt', response.accessToken);
      }
      
      // Get user email from the response
      const userEmail = response.account?.username || response.account?.name;
      
      if (!userEmail) {
        throw new Error('Could not retrieve email from Microsoft account');
      }

      const { data } = await azureVerification(userEmail);
      console.log('✅ Azure verification response:', data);
      
      if (!data.status) {
        console.log('❌ Azure verification failed');
        setLoading(false);
        setIsWidgetOpen(false);
        setShowErrorScreen(true);
        return;
      }
      
      console.log('✅ Azure verification successful, starting Magic OTP flow...');
      // Continue with Magic authentication flow
      await handleMagicAuth(userEmail);
      
    } catch (err: any) {
      console.error('❌ Microsoft OAuth error:', err);
      setShowErrorScreen(true);
      setLoading(false);
      setIsWidgetOpen(false);
    }
  };

  const handleMagicAuth = async (email: string, options?: { forceNew?: boolean }) => {
    if (
      !options?.forceNew &&
      canReuseOtpSession(email, tokenPromise)
    ) {
      dispatch.auth.setEmail(email);
      navigate('/verify-email', { replace: true });
      return;
    }

    if (tokenPromise) {
      await cancelMagicOtpSession(tokenPromise);
      dispatch.auth.clearOtpSession();
    }

    const token: any = prepareMagicOtpToken(
      magic?.auth.loginWithEmailOTP({
        email,
        showUI: false,
        lifespan: MAGIC_OTP_LIFESPAN_SEC,
      })
    );
    dispatch.auth.setEmail(email);
    token
      .on('email-otp-sent', () => {
        markOtpSent(email);
        dispatch.auth.setTokenPromise(token);
        setLoading(false);
        setIsWidgetOpen(false);
        navigate('/verify-email', { replace: true });
      })
      // @ts-ignore
      ?.on('error', () => {
        setShowErrorScreen(true);
        setLoading(false);
        setIsWidgetOpen(false);
      });
  };

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: schema,
    onSubmit: async values => {
      try {
        console.log('Sending email otp');
        setLoading(true);
        formik.setErrors({ email: '' });
        const email = values.email.trim();

        if (canReuseOtpSession(email, tokenPromise)) {
          dispatch.auth.setEmail(email);
          navigate('/verify-email', { replace: true });
          return;
        }

        if (tokenPromise) {
          await cancelMagicOtpSession(tokenPromise);
          dispatch.auth.clearOtpSession();
        }
        clearOtpSessionStorage();

        const { data } = await emailVerification(email);
        if (!data.status) {
          setLoading(false);
          setShowErrorScreen(true);
          return;
        }

        await handleMagicAuth(email, { forceNew: true });
      } catch (err: any) {
        console.log(err.message);
        setShowErrorScreen(true);
        setLoading(false);
      } finally {
        // setLoading(false);
      }
    },
  });

  // Show error screen if there's an authentication error
  if (showErrorScreen) {
    return (
      <div className='flex flex-col items-center justify-center h-[600px] bg-white'>
        <div className='bg-white rounded-xl'>
          <Typography variant='h1' className='title text-center !text-[40px] mb-4'>
            Invalid Login
          </Typography>
          <Typography className='text-center text-[#636366] text-[15px] font-medium mb-6'>
            Please verify your credentials and try again or{' '}
            <a 
              href='mailto:support@kresus.com?subject=Login Support Request'
              className='underline text-[#636366] hover:text-blue-800'
            >
              contact support.
            </a>
          </Typography>
          <div className='flex justify-center'>
            <button
              onClick={() => {
                setShowErrorScreen(false);
                formik.resetForm();
              }}
              className='bg-white border px-4 py-2 text-center items-center justify-center  border-[#4898F3] font-[660] text-black hover:bg-blue-50 transition-colors rounded-full'
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col items-center pt-[90px] px-[188px] h-[638px]'>
      <Typography variant='h1' className='title'>
        Log In
      </Typography>
      <Typography
        variant='h3'
        className='max-w-[35ch] text-center font-[264] mt-[25px]'
      >
        Immutable and verifiable attestation creation powered by the blockchain.
      </Typography>
      <Typography color='grey' className='mt-10'>
        Enter your email below to log in:
      </Typography>
      <TextField
        label='Email'
        type='email'
        name='email'
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        showButton
        onSubmit={formik.handleSubmit}
        className='min-w-[488px] mt-[15px] border border-[#4898F3]'
        buttonDisabled={loading}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            formik.handleSubmit();
          }
        }}
        error={
          formik.touched.email && formik.errors.email ? formik.errors.email : ''
        }
        onClear={() => formik.setFieldValue('email', '')}
      />
      {loading && (
        <div className='flex items-center justify-center mt-5'>
          <ClipLoader size={30} color='#030A74' />
        </div>
      )}
      <div className='flex flex-col items-center mt-[40px]'>
        <div className='bg-[#FAFAFC] rounded-xl border border-[#D1D1D6] p-4 min-w-[488px]'>
          <Typography color='grey' className='text-center text-[15px]  font-medium  mb-4'>
            Other ways to sign in:
          </Typography>
          <button 
            onClick={handleMicrosoftLogin}
            disabled={isWidgetOpen || loading}
            className={`flex items-center justify-center gap-3 min-w-[448px] bg-[#FFFFFF] h-[56px] bg-white border border-k-input-border rounded-full py-4 px-4 transition-colors ${
              isWidgetOpen || loading 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-gray-50'
            }`}
          >
            <img src={ASSETS.microsoft} alt='Microsoft' className='w-5 h-5' />
            <Typography className='text-black font-semibold text-[15px]'>
              Sign in with Microsoft
            </Typography>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
